/**
 * FX service — single source for PLOTFX + FXFWD utilities.
 *
 * Consolidates:
 *   - normalizeFxCurrency           (bare / USD-prefixed / USD-suffixed token → canonical curve)
 *   - PLOTFX parsing + URL building (parsePlotFxPhrase, parseStructure, buildFxPlotUrl, openFxPlot,
 *                                    parseFxPlotQuery, encodeStructure, structureLegLabel,
 *                                    buildFxPlotTitle, swapBasisData, isoDateToLeg)
 *   - PLOTFX history fetcher        (fetchFxPlotHistory, rangeToPlotEnd, structureToResInfo,
 *                                    isEveryRowFieldNull)
 *   - FXFWD last-opened storage     (getLastFxfwdCurrency / setLastFxfwdCurrency /
 *                                    clearLastFxfwdCurrency)
 */

import { popupSpecsForDes } from 'lib/constants';
import { FX_DEFAULT_FIELD, FX_DEFAULT_RANGE_LABEL, FX_FIELD_FROM_QUERY, FX_FIELD_QUERY, FX_FIELDS } from 'lib/fx-plot-constants';
import {
    calculateNdfZeroRatesHistory,
    calculateNdFwdRatesHistory
} from 'services/ndf.service';
import * as HttpService from 'services/http.service';
import * as URL from 'services/url.service';
import { parseSingle, TODAY } from 'helper/advancedDatePicker.helper';

// ---------------------------------------------------------------------------
// Currency normalisation
// ---------------------------------------------------------------------------

/**
 * Normalize an FXFWD currency token into its canonical NDF curve identifiers.
 * Accepts bare ("INR"), USD-prefixed ("USDINR"), or USD-suffixed ("INRUSD").
 * Returns { bare, fxTicker, valid }. Bare "USD" is rejected.
 */
export function normalizeFxCurrency(token) {
    if (typeof token !== 'string') return { bare: null, fxTicker: null, valid: false };
    const t = token.trim().toUpperCase();
    if (!/^[A-Z]{3,6}$/.test(t)) return { bare: null, fxTicker: null, valid: false };

    let bare;
    if (t.length === 3) {
        bare = t;
    } else if (t.length === 6 && t.startsWith('USD')) {
        bare = t.slice(3);
    } else if (t.length === 6 && t.endsWith('USD')) {
        bare = t.slice(0, 3);
    } else if (t.length === 6) {
        // Non-USD cross pair (e.g. AUDJPY). Keep as-is; only supported in spot mode.
        return { bare: t, fxTicker: t, valid: true, isCross: true };
    } else {
        return { bare: null, fxTicker: null, valid: false };
    }

    if (bare === 'USD') return { bare: null, fxTicker: null, valid: false };

    return { bare, fxTicker: `USD${bare}`, valid: true };
}

// ---------------------------------------------------------------------------
// PLOTFX parser + URL builder
// ---------------------------------------------------------------------------

const TENOR_PART = '\\d+(?:\\.\\d+)?[DWMYdwmy]';
const TENOR_GROUPED_RE = /^(\d+(?:\.\d+)?)([DWMY])$/i;
const IMM_PART = '[FGHJKMNQUVXZfghjkmnquvxz]\\d{2}';
const DATE_PART = 'D\\d{8}';
const LEG_PART = `(?:${DATE_PART}|${TENOR_PART}|${IMM_PART})`;
const SINGLE_LEG_RE = new RegExp(`^${LEG_PART}$`);
const FWD_FWD_RE = new RegExp(`^(${LEG_PART})(${LEG_PART})$`);
const IMM_LEG_RE = new RegExp(`^${IMM_PART}$`);
const DATE_LEG_RE = /^D(\d{4})(\d{2})(\d{2})$/;

function normaliseTenor(tenor) {
    if (typeof tenor !== 'string') return null;
    const t = tenor.trim().toUpperCase();
    return SINGLE_LEG_RE.test(t) ? t : null;
}

function legToIso(leg) {
    if (typeof leg !== 'string') return null;
    const m = leg.match(DATE_LEG_RE);
    if (!m) return null;
    return `${m[1]}-${m[2]}-${m[3]}`;
}

export function isoDateToLeg(iso) {
    if (typeof iso !== 'string') return null;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    return `D${m[1]}${m[2]}${m[3]}`;
}

export function parseStructure(token) {
    if (typeof token !== 'string') return null;
    const raw = token.trim().toUpperCase();
    if (!raw) return null;

    const singleTenor = normaliseTenor(raw);
    if (singleTenor) {
        const endDate = legToIso(singleTenor);
        return {
            kind: 'single',
            tenor: endDate ? null : singleTenor,
            fwdStart: null,
            startDate: null,
            endDate,
            raw: singleTenor
        };
    }

    const m = raw.match(FWD_FWD_RE);
    if (m) {
        const fwdStart = normaliseTenor(m[1]);
        const tenor = normaliseTenor(m[2]);
        if (fwdStart && tenor) {
            const startDate = legToIso(fwdStart);
            const endDate = legToIso(tenor);
            return {
                kind: 'fwdFwd',
                fwdStart: startDate ? null : fwdStart,
                tenor: endDate ? null : tenor,
                startDate,
                endDate,
                raw: `${fwdStart}${tenor}`
            };
        }
    }
    return null;
}

function resolveField(token) {
    if (token === undefined || token === null || token === '') return FX_DEFAULT_FIELD;
    const upper = String(token).toUpperCase();
    if (upper in FX_FIELDS) return FX_FIELDS[upper];
    return null;
}

export function parsePlotFxPhrase(phrase) {
    if (typeof phrase !== 'string') return { ok: false, error: 'EMPTY' };
    const tokens = phrase.trim().split(/\s+/).filter(Boolean);
    if (!tokens.length) return { ok: false, error: 'EMPTY' };

    if (tokens[0].toUpperCase() === 'PLOTFX') tokens.shift();
    if (tokens.length === 0) return { ok: false, error: 'NEEDS_CCY_AND_STRUCTURE' };

    const firstAsStructure = parseStructure(tokens[0]);
    let currency = null;
    let structure;
    let structureToken;
    let fieldToken;
    if (firstAsStructure) {
        structure = firstAsStructure;
        [structureToken, fieldToken] = tokens;
    } else {
        const [ccyToken, st, ft] = tokens;
        structureToken = st;
        fieldToken = ft;
        const { bare, valid, isCross } = normalizeFxCurrency(ccyToken);
        if (!valid) return { ok: false, error: 'INVALID_CURRENCY', token: ccyToken };
        currency = bare;
        if (!structureToken) {
            // PLOTFX <currency> → spot mode (currency_spots time series)
            return { ok: true, currency, mode: 'spot' };
        }
        // Forwards/YLD/BASIS require a USD pair — crosses only work as spot.
        if (isCross) return { ok: false, error: 'INVALID_CURRENCY', token: ccyToken };
        structure = parseStructure(structureToken);
        if (!structure) return { ok: false, error: 'INVALID_STRUCTURE', token: structureToken };
    }

    const field = resolveField(fieldToken);
    if (!field) return { ok: false, error: 'INVALID_FIELD', token: fieldToken };

    return { ok: true, currency, structure, field };
}

export function encodeStructure(structureOrString) {
    if (typeof structureOrString === 'string') return structureOrString.toUpperCase();
    if (!structureOrString) return '';
    const legToken = (tenor, iso) => (iso ? isoDateToLeg(iso) : (tenor || '')).toUpperCase();
    if (structureOrString.kind === 'fwdFwd') {
        const start = legToken(structureOrString.fwdStart, structureOrString.startDate);
        const end = legToken(structureOrString.tenor, structureOrString.endDate);
        if (!start || !end) return '';
        return `${start}${end}`;
    }
    return legToken(structureOrString.tenor, structureOrString.endDate);
}

const FX_PLOT_BASE_PATH = 'plot/fx';

export function buildFxPlotUrl({ currency, structure, field = FX_DEFAULT_FIELD, range = FX_DEFAULT_RANGE_LABEL, origin, extra } = {}) {
    if (!currency) return null;
    const { bare, valid, isCross } = normalizeFxCurrency(currency);
    if (!valid) return null;
    if (isCross) return null;

    const structSlug = encodeStructure(structure);
    if (!structSlug) return null;

    const fieldQuery = FX_FIELD_QUERY[field] || FX_FIELD_QUERY[FX_DEFAULT_FIELD];
    const base = origin ?? (process.env.NEXT_PUBLIC_UI_URL || '/');

    const qs = new URLSearchParams();
    qs.set('structure', structSlug);
    qs.set('field', fieldQuery);
    if (range) qs.set('range', range);
    if (extra && typeof extra === 'object') {
        Object.entries(extra).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
        });
    }

    return `${base}${FX_PLOT_BASE_PATH}/${bare}?${qs.toString()}`;
}

export function openFxPlot({ mode = 'same', ...args } = {}) {
    const url = buildFxPlotUrl(args);
    if (!url) return null;
    if (typeof window !== 'undefined') {
        const target = mode === 'new-tab' ? '_blank' : '_self';
        window.open(url, target, popupSpecsForDes);
    }
    return url;
}

export function parseFxPlotQuery({ structure: structureRaw, field: fieldRaw, range } = {}) {
    const structure = parseStructure(structureRaw);
    const fieldKey = fieldRaw ? FX_FIELD_FROM_QUERY[String(fieldRaw).toLowerCase()] : FX_DEFAULT_FIELD;
    return {
        structure,
        field: fieldKey || FX_DEFAULT_FIELD,
        range: range || FX_DEFAULT_RANGE_LABEL
    };
}

export function swapBasisData(data) {
    if (!data) return data;
    const flippedBasis = Array.isArray(data.basis)
        ? data.basis.map((p) => ({ ...p, v: -p.v }))
        : data.basis;
    return {
        ...data,
        leg1: data.leg2,
        leg2: data.leg1,
        basis: flippedBasis
    };
}

const isoToDisplay = (iso) => {
    if (typeof iso !== 'string') return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1].slice(-2)}` : iso;
};

export function structureLegLabel(leg, iso) {
    if (iso) return isoToDisplay(iso);
    return leg || '';
}

export function buildFxPlotTitle({ currency, structure, field }) {
    if (!currency || !structure) return '';
    const startStr = structureLegLabel(structure.fwdStart, structure.startDate);
    const endStr = structureLegLabel(structure.tenor, structure.endDate);
    const tenorStr = structure.kind === 'fwdFwd'
        ? `${startStr} FWD ${endStr}`
        : endStr;
    const fieldLabel = field === FX_FIELDS.PTS_YLD ? 'PTS + YLD' : field;
    return `FX ${currency.toUpperCase()} ${tenorStr} (${fieldLabel})`;
}

// ---------------------------------------------------------------------------
// PLOTFX history fetcher
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

const RANGE_DAYS = Object.freeze({
    '1D': 1,
    '1W': 7,
    '1M': 30,
    '6M': 180,
    YTD: null,
    '1Y': 365,
    '2Y': 730,
    '5Y': 1825,
    MAX: null
});

export function rangeToPlotEnd(range, now = Date.now()) {
    const rangeLabel = range?.toUpperCase();
    if (!rangeLabel || rangeLabel === 'MAX') return null;
    if (rangeLabel === 'YTD') {
        return new Date(new Date(now).getFullYear(), 0, 1).getTime();
    }
    const days = RANGE_DAYS[rangeLabel];
    if (days == null) return null;
    return now - days * DAY_MS;
}

function formatTenor(tenor) {
    const s = String(tenor || '').toUpperCase();
    if (IMM_LEG_RE.test(s)) return s;
    const m = s.match(TENOR_GROUPED_RE);
    if (!m) return null;
    return `${m[1]} ${m[2]}`;
}

export function structureToResInfo(structure, metadata = 'plotfx') {
    if (!structure) return null;
    const hasStartDate = !!structure.startDate;
    const hasEndDate = !!structure.endDate;
    if (structure.kind === 'single' && !hasEndDate) {
        return { kind: 'zero', resInfo: [{ metadata, tenor: structure.tenor }] };
    }
    if (structure.kind === 'single' && hasEndDate) {
        return {
            kind: 'fwd',
            resInfo: [{
                metadata,
                startTenor: '0 D',
                endDate: structure.endDate
            }]
        };
    }
    const item = { metadata };
    if (hasStartDate) item.startDate = structure.startDate;
    else {
        const startTenor = formatTenor(structure.fwdStart);
        if (startTenor == null) return null;
        item.startTenor = startTenor;
    }
    if (hasEndDate) item.endDate = structure.endDate;
    else {
        const endTenor = formatTenor(structure.tenor);
        if (endTenor == null) return null;
        item.endTenor = endTenor;
    }
    return { kind: 'fwd', resInfo: [item] };
}

function snapshotTime(s) {
    if (!s?.date) return null;
    const t = Date.parse(s.date);
    return Number.isNaN(t) ? null : t;
}

function extractFromSnapshots(snapshots, pick) {
    if (!Array.isArray(snapshots)) return [];
    const out = [];
    snapshots.forEach((snap) => {
        const t = snapshotTime(snap);
        if (t == null) return;
        const row = snap.data?.[0];
        if (!row) return;
        const v = pick(row, snap);
        // `fmtPct` yields '' for a missing/non-finite value, and Number('') is 0 —
        // without this guard an absent field would plot as a real 0 datapoint.
        if (v === null || v === undefined || v === '' || Number.isNaN(Number(v))) return;
        out.push({ t, v: Number(v) });
    });
    out.sort((a, b) => a.t - b.t);
    return out;
}

const EMPTY_SERIES = Object.freeze({ pts: [], yld: [], out: [], basis: [], leg1: [], leg2: [] });

export function isEveryRowFieldNull(snapshots, key) {
    if (!Array.isArray(snapshots) || snapshots.length === 0) return false;
    let sawRow = false;
    for (let i = 0; i < snapshots.length; i += 1) {
        const row = snapshots[i]?.data?.[0];
        if (!row) continue;
        sawRow = true;
        const v = row[key];
        if (v !== null && v !== undefined && Number.isFinite(Number(v))) return false;
    }
    return sawRow;
}

const fmtPct = (v, scale = 100, dp = 4) => (Number.isFinite(v) ? `${(Number(v) * scale).toFixed(dp)}` : '');

const readSnapshots = (res) => res?.data?.entity?.snapshots || res?.data?.snapshots || [];

function buildBasisSeries(snapshots, matchedYieldMissing) {
    const leg1 = extractFromSnapshots(snapshots, (r) => fmtPct(r.matchedYield));
    const leg2 = extractFromSnapshots(snapshots, (r) => fmtPct(r.irsRate));
    const basis = extractFromSnapshots(snapshots, (r) => r.basisBp);
    return { ...EMPTY_SERIES, leg1, leg2, basis, matchedYieldMissing };
}

export async function fetchFxPlotHistory({ fxTicker, structure, field, range, signal }) {
    const emptyResult = { ...EMPTY_SERIES, matchedYieldMissing: false };
    if (!fxTicker || !structure || !field) return emptyResult;
    const mapped = structureToResInfo(structure);
    if (!mapped) return emptyResult;

    const plotEnd = rangeToPlotEnd(range);
    const reqConfig = signal ? { signal } : undefined;
    const basePayload = {
        fxTicker,
        resInfo: mapped.resInfo,
        ...(plotEnd ? { plotEnd } : {})
    };

    const fetcher = mapped.kind === 'zero' ? calculateNdfZeroRatesHistory : calculateNdFwdRatesHistory;
    const res = await fetcher(basePayload, reqConfig);
    const snapshots = readSnapshots(res);
    const matchedYieldMissing = isEveryRowFieldNull(snapshots, 'matchedYield');

    if (field === FX_FIELDS.BASIS) return buildBasisSeries(snapshots, matchedYieldMissing);

    if (mapped.kind === 'zero') {
        return {
            ...EMPTY_SERIES,
            pts: extractFromSnapshots(snapshots, (r) => fmtPct(r.rawFwd, 1)),
            yld: extractFromSnapshots(snapshots, (r) => fmtPct(r.matchedYield, 100, 3)),
            out: extractFromSnapshots(snapshots, (r) => fmtPct(r.outright, 1)),
            matchedYieldMissing
        };
    }
    return {
        ...EMPTY_SERIES,
        pts: extractFromSnapshots(snapshots, (r) => fmtPct(r.rawFwdPoints, 1)),
        yld: extractFromSnapshots(snapshots, (r) => fmtPct(r.matchedYield)),
        out: extractFromSnapshots(snapshots, (r) => fmtPct(r.outright, 1)),
        matchedYieldMissing
    };
}

export async function fetchFxSpotHistory({ fxTicker }) {
    if (!fxTicker) return { rows: [] };
    const res = await HttpService.postWithAuth(URL.FETCH_CURRENCY_SPOT_HISTORY(), { fxTicker });
    const rows = res?.data?.entity;
    return { rows: Array.isArray(rows) ? rows : [] };
}

// ---------------------------------------------------------------------------
// FXFWD last-opened currency (per-user client-side persistence)
// ---------------------------------------------------------------------------

const FXFWD_LAST_KEY_PREFIX = 'fxfwd:lastCurrency:';

const fxfwdKeyFor = (userId) => `${FXFWD_LAST_KEY_PREFIX}${userId || 'anon'}`;

function withStorage(fn, fallback = undefined) {
    if (typeof window === 'undefined') return fallback;
    try {
        return fn(window.localStorage);
    } catch (e) {
        return fallback;
    }
}

export function getLastFxfwdCurrency(userId) {
    return withStorage((ls) => {
        const v = ls.getItem(fxfwdKeyFor(userId));
        return typeof v === 'string' && v ? v : null;
    }, null);
}

export function setLastFxfwdCurrency(userId, currency) {
    if (typeof currency !== 'string' || !currency) return;
    withStorage((ls) => ls.setItem(fxfwdKeyFor(userId), currency.toUpperCase()));
}

export function clearLastFxfwdCurrency(userId) {
    withStorage((ls) => ls.removeItem(fxfwdKeyFor(userId)));
}

// ---------------------------------------------------------------------------
// FXFWD Custom Structure — Start/End input parsing (shared with WATCHLIST NDF)
// ---------------------------------------------------------------------------

const DATE_DDMMYY_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;
const DATE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export const fmtDDMMYY = (iso) => {
    if (!iso) return '';
    const m = iso.match(DATE_ISO_RE);
    if (!m) return iso;
    return `${m[3]}/${m[2]}/${m[1].slice(-2)}`;
};

export const parseDateInput = (raw) => {
    let dd; let mm; let yyyy;
    const dm = raw.match(DATE_DDMMYY_RE);
    if (dm) {
        dd = parseInt(dm[1], 10);
        mm = parseInt(dm[2], 10);
        yyyy = dm[3].length === 2 ? 2000 + parseInt(dm[3], 10) : parseInt(dm[3], 10);
    } else {
        const im = raw.match(DATE_ISO_RE);
        if (!im) return null;
        yyyy = parseInt(im[1], 10);
        mm = parseInt(im[2], 10);
        dd = parseInt(im[3], 10);
    }
    if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
    const d = new Date(Date.UTC(yyyy, mm - 1, dd));
    if (d.getUTCFullYear() !== yyyy || d.getUTCMonth() !== mm - 1 || d.getUTCDate() !== dd) return null;
    const iso = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    return { display: fmtDDMMYY(iso), iso };
};

const buildDateSide = (date) => {
    const today = TODAY();
    const effective = date < today ? today : date;
    const yyyy = effective.getFullYear();
    const mm = String(effective.getMonth() + 1).padStart(2, '0');
    const dd = String(effective.getDate()).padStart(2, '0');
    const iso = `${yyyy}-${mm}-${dd}`;
    return { display: fmtDDMMYY(iso), iso };
};

export const resolveStructureSide = (rawInput, { side }) => {
    if (!rawInput) return null;
    const trimmed = String(rawInput).trim();
    if (!trimmed) return null;

    if (trimmed.includes('/') || trimmed.includes('-')) {
        const parsed = parseDateInput(trimmed);
        if (!parsed) return null;
        const [y, m, d] = parsed.iso.split('-').map(Number);
        return buildDateSide(new Date(y, m - 1, d));
    }

    const nlDate = parseSingle(trimmed, new Date());
    if (nlDate) return buildDateSide(nlDate);

    const upper = trimmed.toUpperCase();

    if (upper === 'SPOT') {
        if (side === 'end') return null;
        return { display: 'SPOT', api: '0 D' };
    }

    const imm = upper.match(/^([HMUZ])(\d{2})$/);
    if (imm) {
        return { display: `${imm[1]}${imm[2]}`, api: `${imm[1]}${imm[2]}` };
    }

    const tenor = upper.match(/^(\d+)\s*([DWMY])$/);
    if (tenor) {
        return {
            display: `${tenor[1]}${tenor[2]}`,
            api: `${tenor[1]} ${tenor[2]}`
        };
    }

    return null;
};

export function buildFxStructureFromLegs({ start, end } = {}) {
    if (!start || !end) return null;
    const startSide = resolveStructureSide(start, { side: 'start' });
    const endSide = resolveStructureSide(end, { side: 'end' });
    if (!endSide) return null;
    const startIsSpot = startSide && startSide.api === '0 D';
    if (startIsSpot || !startSide) {
        return {
            kind: 'single',
            tenor: endSide.iso ? null : endSide.api?.replace(/\s+/g, ''),
            fwdStart: null,
            startDate: null,
            endDate: endSide.iso || null,
            raw: endSide.iso ? endSide.iso : endSide.api
        };
    }
    return {
        kind: 'fwdFwd',
        fwdStart: startSide.iso ? null : startSide.api?.replace(/\s+/g, ''),
        tenor: endSide.iso ? null : endSide.api?.replace(/\s+/g, ''),
        startDate: startSide.iso || null,
        endDate: endSide.iso || null,
        raw: `${startSide.iso || startSide.api}-${endSide.iso || endSide.api}`
    };
}
